/**
 * Phase 4b PR 3 — useUser hook.
 *
 * Thin wrapper around the AuthContext exposing only the bits a
 * consumer typically needs. Use this from components that care about
 * the current athlete (chat shell, dashboard, etc.).
 *
 * For lower-level access (the SupabaseClient itself, the full Session
 * object), import `useAuthContext` from `../contexts/AuthContext`.
 *
 * Usage:
 *   const { user, loading, error } = useUser();
 *   if (loading) return <Spinner />;
 *   if (error) return <ErrorView />;
 *   if (!user) return <Navigate to="/landing" replace />;
 *   return <ChatShell user={user} />;
 */

import type { User } from '@supabase/supabase-js';
import { useAuthContext } from '../contexts/AuthContext';

export interface UseUserResult {
  /** Currently signed-in user, or null if not signed in / still loading. */
  user: User | null;
  /** True until the initial auth handshake completes. */
  loading: boolean;
  /** Non-null if auth init failed (e.g., /api/config unreachable). */
  error: Error | null;
  /** Convenience signOut — calls supabase.auth.signOut(). */
  signOut: () => Promise<void>;
}

export function useUser(): UseUserResult {
  const { user, loading, error, signOut } = useAuthContext();
  return { user, loading, error, signOut };
}
